import { useCallback, useRef } from 'react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'

import { useLenis } from '@/contexts/lenis'
import { useMotionTouchMedia } from '@/hooks'
import { useLoaderStore } from '@/store/loader'

import {
  createLinesMotion,
  createMaskMotion,
  createWordsMotion,
  getScrollTriggerOptions,
  Motion
} from './shared'

export const useDoubleTextMotion = () => {
  const motionRef = useRef<HTMLDivElement>(null)
  const scrollTrigger = useRef<ScrollTrigger>(null!)
  const fontsReady = useLoaderStore((state) => state.fontsReady)
  const { lenis } = useLenis()

  const titleMotion = useRef<Motion | null>(null)
  const pretitleMotion = useRef<Motion | null>(null)
  const paragraphLMotion = useRef<Motion | null>(null)
  const paragraphRMotion = useRef<Motion | null>(null)

  const revertSplitText = useCallback(() => {
    titleMotion.current?.revert()
    pretitleMotion.current?.revert()
    paragraphLMotion.current?.revert()
    paragraphRMotion.current?.revert()
  }, [])

  /**
   * reveal setup
   */
  useMotionTouchMedia(
    {
      pointer({ selector }) {
        if (!fontsReady || !lenis) return

        const background = selector?.('[data-motion-background]')[0]
        const title = selector?.('[data-motion-title]')[0]
        const pretitle = selector?.('[data-motion-pretitle]')[0]
        const paragraphL = selector?.('[data-motion-l-paragraph]')[0]
        const paragraphR = selector?.('[data-motion-r-paragraph]')[0]
        const paragraphDelay = background || pretitle || title ? 0.2 : 0

        titleMotion.current = title ? createWordsMotion(title) : null
        pretitleMotion.current = pretitle ? createMaskMotion(pretitle) : null
        paragraphLMotion.current = paragraphL ? createLinesMotion(paragraphL) : null
        paragraphRMotion.current = paragraphR ? createLinesMotion(paragraphR) : null

        const animation = gsap.timeline({ onComplete: revertSplitText })
        titleMotion.current && animation.add(titleMotion.current.timeline, 0)
        pretitleMotion.current && animation.add(pretitleMotion.current.timeline, 0.1)
        paragraphLMotion.current && animation.add(paragraphLMotion.current.timeline, paragraphDelay)
        paragraphRMotion.current &&
          animation.add(paragraphRMotion.current.timeline, paragraphDelay + 0.1)
        background &&
          animation.from(
            background,
            {
              opacity: 0,
              scale: 1.2,
              duration: 1,
              ease: 'power3.out'
            },
            0
          )

        scrollTrigger.current = ScrollTrigger.create({
          ...getScrollTriggerOptions({
            trigger: motionRef.current,
            animation
          })
        })
      },

      revert() {
        scrollTrigger.current?.kill()
        revertSplitText()
      }
    },
    { scope: motionRef, dependencies: [fontsReady, lenis] }
  )

  return { motionRef }
}
